
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import Button from '../../components/base/Button';

export default function BookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pickupLocation, setPickupLocation] = useState('الرياض، حي الملز');
  const [destination, setDestination] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('economy');

  const vehicleTypes = [
    {
      id: 'economy',
      name: t('economy'),
      icon: 'ri-car-line',
      price: 15,
      description: 'سيارة اقتصادية مريحة'
    },
    {
      id: 'family',
      name: t('family'),
      icon: 'ri-car-washing-line',
      price: 25,
      description: 'سيارة عائلية واسعة'
    },
    {
      id: 'vip',
      name: t('vip'),
      icon: 'ri-car-fill',
      price: 45,
      description: 'سيارة فاخرة مع خدمة مميزة'
    }
  ];

  const handleConfirmBooking = () => {
    if (!destination.trim()) {
      alert('يرجى إدخال الوجهة');
      return;
    }
    navigate('/booking/confirm', {
      state: {
        pickup: pickupLocation,
        destination,
        vehicleType: selectedVehicle,
        price: vehicleTypes.find(v => v.id === selectedVehicle)?.price || 15
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20">
      <TopNavigation 
        title="حجز رحلة جديدة" 
        showBack 
        onBack={() => navigate('/home')}
      />
      
      <div className="pt-20 px-4">
        {/* Location Inputs */}
        <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm border border-gray-100">
          <div className="space-y-4">
            {/* Pickup Location */}
            <div className="flex items-center">
              <div className="w-3 h-3 bg-[#0056A6] rounded-full mr-3 flex-shrink-0"></div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">{t('pickupLocation')}</p>
                <input
                  type="text"
                  value={pickupLocation}
                  onChange={(e) => setPickupLocation(e.target.value)}
                  className="w-full text-sm font-medium text-gray-800 bg-transparent border-none outline-none"
                  placeholder="موقع الاستلام"
                />
              </div>
            </div>
            
            <div className="border-r border-gray-200 mr-1.5 h-6"></div>
            
            {/* Destination */}
            <div className="flex items-center">
              <div className="w-3 h-3 border-2 border-[#F9A826] rounded-full mr-3 flex-shrink-0"></div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">{t('destination')}</p>
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full text-sm font-medium text-gray-800 bg-transparent border-none outline-none"
                  placeholder={t('enterDestination')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{t('vehicleType')}</h3>
          <div className="space-y-3">
            {vehicleTypes.map((vehicle) => (
              <div
                key={vehicle.id}
                onClick={() => setSelectedVehicle(vehicle.id)}
                className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition-all duration-200 ${
                  selectedVehicle === vehicle.id
                    ? 'border-[#0056A6] bg-[#0056A6]/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
                      selectedVehicle === vehicle.id
                        ? 'bg-[#0056A6] text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <i className={`${vehicle.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">{vehicle.name}</h4>
                      <p className="text-sm text-gray-500">{vehicle.description}</p>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="text-lg font-bold text-[#0056A6]">{vehicle.price} ريال</p>
                    <p className="text-xs text-gray-500">{t('estimatedPrice')}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Booking Options */}
        <div className="space-y-3 mb-6">
          <Button
            onClick={handleConfirmBooking}
            className="w-full"
            size="lg"
          >
            <i className="ri-check-line mr-2"></i>
            {t('confirmRequest')}
          </Button>
          
          <button
            onClick={() => navigate('/booking/bidding', {
              state: { pickup: pickupLocation, destination }
            })}
            className="w-full bg-[#F9A826] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#E8971F] transition-colors duration-200"
          >
            <i className="ri-auction-line mr-2"></i>
            {t('biddingRequest')}
          </button>
        </div>

        {/* Quick Destinations */}
        <div>
          <h3 className="text-lg font-bold text-gray-800 mb-4">وجهات سريعة</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { name: 'مطار الملك خالد', icon: 'ri-plane-line' },
              { name: 'مول الرياض', icon: 'ri-shopping-bag-line' },
              { name: 'مستشفى الملك فهد', icon: 'ri-hospital-line' },
              { name: 'جامعة الملك سعود', icon: 'ri-school-line' }
            ].map((place, index) => (
              <button
                key={index}
                onClick={() => setDestination(place.name)}
                className="bg-white rounded-xl p-4 border border-gray-100 hover:border-[#0056A6] transition-all duration-200 text-center"
              >
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <i className={`${place.icon} text-gray-600`}></i>
                </div>
                <p className="text-sm font-medium text-gray-800">{place.name}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
